import {QueryClient} from '@tanstack/react-query'

const defaultQueryOptions = {
    queries: {
        staleTime: 60 * 1000,
        gcTime: 300 * 1000,
        retry: (failureCount: number, error: any) => {
            if (error && typeof error === 'object' && 'status' in error) {
                const status = (error as any).status
                if (status >= 400 && status < 500) return false
            }
            return failureCount < 2
        },
        retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        refetchOnMount: true,
    },
    mutations: {
        retry: 1,
        retryDelay: 1000,
    },
} as const

// Each server render builds its own client. A finite gcTime there arms a timer per query that keeps
// the whole render (React tree, messages, response stream) in memory until it fires, so server
// queries keep TanStack's server default of Infinity even when a hook sets its own gcTime.
function withoutServerGcTimers(client: QueryClient): QueryClient {
    const defaultOptions = client.defaultQueryOptions.bind(client)
    client.defaultQueryOptions = options => ({...defaultOptions(options), gcTime: Infinity})
    return client
}

function makeQueryClient(): QueryClient {
    return new QueryClient({
        defaultOptions: defaultQueryOptions,
    })
}

let browserQueryClient: QueryClient | undefined

export function getQueryClient(): QueryClient {
    if (typeof window === 'undefined') {
        return withoutServerGcTimers(makeQueryClient())
    }
    if (!browserQueryClient) {
        browserQueryClient = makeQueryClient()
    }
    return browserQueryClient
}
